import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { motion } from 'motion/react';
import { databaseService, hashPassword } from '../services/database';
import {
  customerLogin,
  exchangeOAuthLoginCode,
  persistCustomerSessionTokens,
  CUSTOMER_ACCESS_TOKEN_KEY,
  CUSTOMER_REFRESH_TOKEN_KEY,
} from '../services/auth/customerAuthApi';
import { toast } from 'sonner@2.0.3';
import { isBackendUserDbEnabled } from '../services/backendMode';
import {
  useCustomerSessionIdle,
  touchSessionActivity,
  clearSessionActivityTimestamp,
} from '../services/auth/sessionIdle';
import { setAcceptedLegalVersions } from '../lib/consentStorage';
import { resetOnboardingCompleteGate } from '../lib/onboardingLifecycle';
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from '../lib/legalVersions';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function mapLoginResponseToUser(data: {
  user: { id: string; email: string; name: string; role: string };
}): User {
  const r = data.user.role;
  return {
    id: data.user.id,
    email: data.user.email,
    name: data.user.name,
    role:
      r === 'customer_admin' || r === 'admin'
        ? 'Customer Admin'
        : r === 'tenant_owner'
          ? 'Tenant Owner'
          : r === 'viewer' || r === 'member'
            ? 'User'
            : r === 'devops'
              ? 'DevOps'
              : r === 'billing_manager'
                ? 'Billing'
                : 'Customer Admin',
  };
}

const mapDbUserToSessionUser = (dbUser: {
  id?: string;
  email: string;
  name: string;
  account_type?: 'personal' | 'work';
}): User => {
  const email = dbUser.email.trim().toLowerCase();
  const role =
    email === 'demo@demo.com'
      ? 'Super Admin'
      : dbUser.account_type === 'personal'
        ? 'User'
        : 'Customer Admin';
  return {
    id: dbUser.id || `user_${Date.now()}`,
    email: dbUser.email,
    name: dbUser.name,
    role,
  };
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        // Platform API mode requires a stored access token; otherwise clear stale "logged in" state.
        if (isBackendUserDbEnabled() && !localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY)) {
          localStorage.removeItem('coremonit_user');
          setUser(null);
          setIsLoading(false);
          return;
        }
        const savedUser = localStorage.getItem('coremonit_user');
        if (savedUser) {
          const parsed = JSON.parse(savedUser) as User;
          if (parsed?.email?.toLowerCase() === 'demo@demo.com') {
            setAcceptedLegalVersions(CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION);
          }
          setUser(parsed);
        }
      } catch (error) {
        console.error('Error loading saved user session:', error);
        localStorage.removeItem('coremonit_user');
      } finally {
        setIsLoading(false);
      }
    };

    // Simulate loading time
    setTimeout(checkExistingSession, 500);
  }, []);

  /** OAuth redirect returns ?oauth_exchange=... or ?signin=oauth_error — complete session without password. */
  useEffect(() => {
    if (isLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('signin') === 'oauth_error') {
      const reason = params.get('reason') || 'unknown';
      toast.error(`Google sign-in failed: ${decodeURIComponent(reason)}`);
      params.delete('signin');
      params.delete('reason');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
      return;
    }
    const ex = params.get('oauth_exchange');
    if (!ex || !isBackendUserDbEnabled()) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await exchangeOAuthLoginCode(ex);
        if (cancelled) return;
        const sessionUser = mapLoginResponseToUser(data);
        persistCustomerSessionTokens(data);
        touchSessionActivity();
        localStorage.setItem('coremonit_user', JSON.stringify(sessionUser));
        setUser(sessionUser);
        params.delete('oauth_exchange');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
        toast.success('Signed in with Google');
      } catch (e) {
        if (!cancelled) {
          console.error('[auth] OAuth exchange failed', e);
          toast.error(e instanceof Error ? e.message : 'Google sign-in could not complete');
        }
        params.delete('oauth_exchange');
        const qs = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoading]);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      if (isBackendUserDbEnabled()) {
        const data = await customerLogin(email, password);
        const sessionUser = mapLoginResponseToUser(data);
        persistCustomerSessionTokens(data);
        touchSessionActivity();
        localStorage.setItem('coremonit_user', JSON.stringify(sessionUser));
        setUser(sessionUser);
        setIsLoading(false);
        return true;
      }

      await new Promise((resolve) => setTimeout(resolve, 500));
      const dbUser = await databaseService.getUserByEmail(normalizedEmail);
      if (dbUser) {
        const passwordMatches = dbUser.password_hash === hashPassword(password);
        const isVerified = dbUser.email_verified && dbUser.mobile_verified;
        if (passwordMatches && isVerified) {
          const sessionUser = mapDbUserToSessionUser(dbUser);
          if (normalizedEmail === 'demo@demo.com') {
            setAcceptedLegalVersions(CURRENT_TERMS_VERSION, CURRENT_PRIVACY_VERSION);
          }
          setUser(sessionUser);
          localStorage.setItem('coremonit_user', JSON.stringify(sessionUser));
          setIsLoading(false);
          return true;
        }
      }

      setIsLoading(false);
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      return false;
    }
  };

  const logout = useCallback(() => {
    resetOnboardingCompleteGate();
    setUser(null);
    localStorage.removeItem('coremonit_user');
    localStorage.removeItem(CUSTOMER_ACCESS_TOKEN_KEY);
    localStorage.removeItem(CUSTOMER_REFRESH_TOKEN_KEY);
    clearSessionActivityTimestamp();
  }, []);

  useCustomerSessionIdle(
    Boolean(isBackendUserDbEnabled() && user),
    logout
  );

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem('coremonit_user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <motion.div
          className="relative"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin" />
          <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-blue-500 rounded-full animate-spin animation-delay-150" />
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 text-center">
            <div className="text-lg font-semibold bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              CoreMonit
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Loading platform...
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}